import { getWpBase } from "./domain";

type JsonBody = Record<string, unknown>;
type FetchJSONOptions = Omit<RequestInit, "body"> & {
  baseUrl?: string;
  body?: RequestInit["body"] | JsonBody;
};

export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const DEFAULT_API_BASE_URL = "http://localhost:8000";

const getEnvVar = (key: string) => {
  if (typeof process !== "undefined" && process.env && process.env[key]) {
    return process.env[key];
  }
  return undefined;
};

const isLocalhost = () => {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
};

export const getApiBaseUrl = () => {
  const env =
    getEnvVar("REACT_APP_API_BASE_URL") ||
    getEnvVar("VITE_API_BASE_URL") ||
    "";
  if (env) return env;
  return isLocalhost() ? DEFAULT_API_BASE_URL : "";
};

export const hasApiBaseUrl = () => Boolean(getApiBaseUrl());

export const getWpBaseUrl = () =>
  getEnvVar("REACT_APP_WP_BASE_URL") || getEnvVar("VITE_WP_BASE_URL") || getWpBase();

const isJsonBody = (body: FetchJSONOptions["body"]) =>
  !!body && typeof body === "object" && !(body instanceof FormData);

const buildUrl = (baseUrl: string, path: string) => {
  if (!path) return baseUrl;
  if (path.startsWith("http")) return path;
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

const withJsonBody = (options: FetchJSONOptions = {}): RequestInit => {
  const { body, ...rest } = options;
  if (!isJsonBody(body)) {
    return { ...rest, body: body as BodyInit | null | undefined };
  }
  const headers = new Headers(rest.headers || {});
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return {
    ...rest,
    headers,
    body: JSON.stringify(body)
  };
};

export async function fetchJSON<T>(
  path: string,
  options: FetchJSONOptions = {}
): Promise<T> {
  const { baseUrl = getApiBaseUrl(), ...requestOptions } = options;
  const prepared = withJsonBody(requestOptions);
  const res = await fetch(buildUrl(baseUrl, path), prepared);

  if (!res.ok) {
    let payload: unknown = undefined;
    try {
      payload = await res.json();
    } catch {
      try {
        payload = await res.text();
      } catch {
        payload = undefined;
      }
    }
    throw new ApiError(res.status, `Request failed: ${res.status}`, payload);
  }

  if (res.status === 204) {
    return null as T;
  }

  return res.json() as Promise<T>;
}

export async function apiFetch<T>(path: string, options: FetchJSONOptions = {}): Promise<T> {
  return fetchJSON<T>(path, {
    ...options,
    baseUrl: getApiBaseUrl(),
    credentials: "include"
  });
}

export async function wpGet<T>(endpoint: string): Promise<T> {
  return fetchJSON<T>(endpoint, { baseUrl: getWpBaseUrl() });
}
